import { DEFAULT_TIMEZONE } from '@yoyoclub/shared';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Get YYYY-MM-DD string in local timezone (avoids UTC conversion issues)
function getLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatPrice(cents: number): string {
  return (cents / 100).toFixed(2);
}

function parseDateString(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

interface Session {
  date: string;
  startTime: string;
  endTime: string;
  // Raw ISO timestamp of session end (UTC). Optional for backward compatibility
  // with tests/legacy callers; when present, used to decide "past" with second
  // precision instead of the day-level `date` fallback.
  endTimestamp?: string;
  // Raw ISO timestamp of session start (UTC). Used with cutoffTimestamp to
  // decide whether the "Registration closes ..." hint is worth showing.
  startTimestamp?: string;
  // Raw ISO timestamp of when registration closes (the API's per-session
  // effectiveBookingCutoffAt). Absent for legacy callers → falls back to the
  // ended check, matching the pre-cutoff behavior.
  cutoffTimestamp?: string;
  maxCapacity: number;
  currentBookings?: number; // Optional for backward compatibility
  price?: number | null; // Session child price in cents (null = use activity default)
  adultPrice?: number | null; // Session adult price in cents (null = use activity default)
  description?: string | null; // Optional session description
}

interface CalendarSessionSelectorProps {
  sessions: Session[];
  selectedSession?: number;
  onSelectSession: (index: number | undefined) => void;
  activityPrice?: number; // Activity default child price in cents
  activityAdultPrice?: number; // Activity default adult price in cents
  childRequired?: boolean; // false = adult-only activity
}

type ViewMode = 'calendar' | 'list';

export default function CalendarSessionSelector({
  sessions,
  selectedSession,
  onSelectSession,
  activityPrice = 0,
  activityAdultPrice = 0,
  childRequired = true,
}: CalendarSessionSelectorProps) {
  const { t, i18n } = useTranslation(['activity', 'common']);
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const [viewMode, setViewMode] = useState<ViewMode>('calendar');

  // Group sessions by date
  const sessionsByDate = useMemo(() => {
    const grouped = new Map<string, { session: Session; index: number }[]>();
    sessions.forEach((session, index) => {
      const dateKey = session.date;
      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      grouped.get(dateKey)!.push({ session, index });
    });
    return grouped;
  }, [sessions]);

  // Calculate spots left (with mock fallback for testing)
  const getSpotsLeft = useCallback((session: Session) => {
    if (session.currentBookings !== undefined) {
      return session.maxCapacity - session.currentBookings;
    }
    // Mock: Return 60% capacity available for testing
    return Math.floor(session.maxCapacity * 0.6);
  }, []);

  // Check if date is in the past (day-level — used for calendar cell styling).
  const isPast = useCallback(
    (date: Date) => {
      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0);
      return checkDate < today;
    },
    [today]
  );

  // Has this session already ended? Prefer the precise ISO timestamp; fall back
  // to the day-level check so legacy callers without `endTimestamp` still work.
  const isSessionEnded = useCallback(
    (session: Session) => {
      if (session.endTimestamp) {
        return new Date(session.endTimestamp).getTime() < Date.now();
      }
      return isPast(parseDateString(session.date));
    },
    [isPast]
  );

  // Registration closed for this session? Closed at exactly the cutoff instant
  // (now >= cutoff). Without a cutoff timestamp this matches the ended check.
  const isSessionClosed = useCallback(
    (session: Session) => {
      if (session.cutoffTimestamp) {
        return Date.now() >= new Date(session.cutoffTimestamp).getTime();
      }
      return isSessionEnded(session);
    },
    [isSessionEnded]
  );

  // Unavailable = ended or registration closed. Drives filtering, selection,
  // and the disabled state everywhere the old "past" check was used.
  const isSessionPast = useCallback(
    (session: Session) => isSessionEnded(session) || isSessionClosed(session),
    [isSessionEnded, isSessionClosed]
  );

  // Pick the best session on a given date for auto-selection.
  // Prefer non-sold-out, non-past sessions; fall back to a sold-out one so the
  // user can still join the waitlist when the whole day is full.
  const pickSelectableSession = useCallback(
    (dateSessions: { session: Session; index: number }[]) => {
      const live = dateSessions.filter(({ session }) => !isSessionPast(session));
      const open = live.find(({ session }) => getSpotsLeft(session) > 0);
      if (open) return open;
      return live[0]; // sold-out fallback, but still live
    },
    [getSpotsLeft, isSessionPast]
  );

  // Find the first selectable session in a given month — prefers a date with
  // an open, not-yet-ended session, otherwise falls back to a date that still
  // has at least one live (sold-out) session (so the user can join the
  // waitlist).
  const findFirstSelectableInMonth = useCallback(
    (year: number, month: number): { date: Date; sessionIndex: number } | null => {
      const monthDateEntries = Array.from(sessionsByDate.keys())
        .map((dateStr) => ({ dateStr, date: parseDateString(dateStr) }))
        .filter(({ date }) => date.getFullYear() === year && date.getMonth() === month)
        .filter(({ date }) => !isPast(date))
        .sort((a, b) => a.date.getTime() - b.date.getTime());

      const open = monthDateEntries.find(({ dateStr }) =>
        sessionsByDate
          .get(dateStr)!
          .some(({ session }) => !isSessionPast(session) && getSpotsLeft(session) > 0)
      );
      const fallback = monthDateEntries.find(({ dateStr }) =>
        sessionsByDate.get(dateStr)!.some(({ session }) => !isSessionPast(session))
      );
      const target = open ?? fallback;
      if (!target) return null;

      const picked = pickSelectableSession(sessionsByDate.get(target.dateStr)!);
      return picked ? { date: target.date, sessionIndex: picked.index } : null;
    },
    [sessionsByDate, isPast, isSessionPast, getSpotsLeft, pickSelectableSession]
  );

  // Get first selectable (non-expired) session date and session index.
  // Prefers an open, live session; falls back to any live session.
  const { firstFutureDate, defaultMonth, firstSelectableSessionIndex } = useMemo(() => {
    const futureDateEntries = Array.from(sessionsByDate.keys())
      .map((dateStr) => ({ dateStr, date: parseDateString(dateStr) }))
      .filter(({ date }) => date >= today)
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    const open = futureDateEntries.find(({ dateStr }) =>
      sessionsByDate
        .get(dateStr)!
        .some(({ session }) => !isSessionPast(session) && getSpotsLeft(session) > 0)
    );
    const fallback = futureDateEntries.find(({ dateStr }) =>
      sessionsByDate.get(dateStr)!.some(({ session }) => !isSessionPast(session))
    );
    const target = open ?? fallback;
    if (target) {
      const picked = pickSelectableSession(sessionsByDate.get(target.dateStr)!);
      return {
        firstFutureDate: target.date,
        defaultMonth: new Date(target.date.getFullYear(), target.date.getMonth(), 1),
        firstSelectableSessionIndex: picked?.index,
      };
    }

    // Fallback: no future sessions at all → show today's month, nothing selected
    return {
      firstFutureDate: null,
      defaultMonth: new Date(today.getFullYear(), today.getMonth(), 1),
      firstSelectableSessionIndex: undefined,
    };
  }, [sessionsByDate, today, isSessionPast, getSpotsLeft, pickSelectableSession]);

  const [currentMonth, setCurrentMonth] = useState(defaultMonth);
  const [selectedDate, setSelectedDate] = useState<Date | null>(firstFutureDate);

  // Auto-select first selectable session on mount only.
  // Runs once — user interactions (navigateMonth, selectDate) manage selection themselves.
  // Sessions data doesn't change within a single mount (fetched once per page load).
  useEffect(() => {
    if (firstSelectableSessionIndex !== undefined && selectedSession === undefined) {
      onSelectSession(firstSelectableSessionIndex);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigate month and auto-select first selectable date + session in target month
  const navigateMonth = useCallback(
    (delta: number) => {
      const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + delta, 1);
      setCurrentMonth(newMonth);

      const result = findFirstSelectableInMonth(newMonth.getFullYear(), newMonth.getMonth());
      if (result) {
        setSelectedDate(result.date);
        onSelectSession(result.sessionIndex);
      } else {
        setSelectedDate(null);
        onSelectSession(undefined);
      }
    },
    [currentMonth, findFirstSelectableInMonth, onSelectSession]
  );

  // Generate calendar grid for current month
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDayOfWeek = firstDay.getDay(); // 0 = Sunday
    const daysInMonth = lastDay.getDate();

    const days: (Date | null)[] = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days in month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  }, [currentMonth]);

  // Check if date has sessions
  const hasSession = useCallback(
    (date: Date) => {
      const dateKey = getLocalDateString(date);
      return sessionsByDate.has(dateKey);
    },
    [sessionsByDate]
  );

  // Format month/year for header
  const formatMonthYear = (date: Date) => {
    return date.toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'long',
    });
  };

  // Format date with weekday for time slot header
  const formatDateWithWeekday = (date: Date) => {
    return date.toLocaleDateString(i18n.language, {
      month: 'short',
      day: 'numeric',
      weekday: 'short',
    });
  };

  // Selectable sessions for the "All Sessions" list view (live, includes
  // sold-out so users can join the waitlist directly from the list).
  // Filters per-session so a today-date with some already-ended sessions only
  // shows the remaining ones.
  const bookableSessions = useMemo(() => {
    const result: {
      dateStr: string;
      date: Date;
      sessions: { session: Session; index: number }[];
    }[] = [];

    const sortedDates = Array.from(sessionsByDate.keys())
      .map((dateStr) => ({ dateStr, date: parseDateString(dateStr) }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    for (const { dateStr, date } of sortedDates) {
      const liveSessions = (sessionsByDate.get(dateStr) ?? []).filter(
        ({ session }) => !isSessionPast(session)
      );
      if (liveSessions.length > 0) {
        result.push({ dateStr, date, sessions: liveSessions });
      }
    }

    return result;
  }, [sessionsByDate, isSessionPast]);

  const selectedDateKey = selectedDate ? getLocalDateString(selectedDate) : undefined;
  const availableSessions = selectedDateKey ? sessionsByDate.get(selectedDateKey) || [] : [];

  // 2024-01-07 is a Sunday; generate Sun–Sat labels in current locale
  const weekDays = Array.from({ length: 7 }, (_, i) =>
    new Date(2024, 0, 7 + i).toLocaleDateString(i18n.language, { weekday: 'narrow' })
  );

  const isAdultOnly = childRequired === false;

  // Select a calendar date and auto-select its first selectable session
  // (open spots preferred; sold-out as a fallback to enable waitlist join).
  const selectDate = useCallback(
    (date: Date) => {
      setSelectedDate(date);
      // Don't auto-select sessions on past dates
      if (isPast(date)) {
        onSelectSession(undefined);
        return;
      }
      const dateKey = getLocalDateString(date);
      const dateSessions = sessionsByDate.get(dateKey);
      if (dateSessions && dateSessions.length > 0) {
        const picked = pickSelectableSession(dateSessions);
        onSelectSession(picked?.index);
      } else {
        onSelectSession(undefined);
      }
    },
    [sessionsByDate, onSelectSession, isPast, pickSelectableSession]
  );

  // Select a session and sync calendar state to its date
  const selectSessionWithSync = useCallback(
    (index: number, sessionDate: Date) => {
      onSelectSession(index);
      setSelectedDate(sessionDate);
      setCurrentMonth(new Date(sessionDate.getFullYear(), sessionDate.getMonth(), 1));
    },
    [onSelectSession]
  );

  // "Registration closes {time}" hint: only when the cutoff is meaningfully
  // earlier than the session start (otherwise the deadline is implicit).
  const registrationClosesLabel = useCallback(
    (session: Session): string | null => {
      if (!session.cutoffTimestamp || !session.startTimestamp) return null;
      const cutoff = new Date(session.cutoffTimestamp);
      if (cutoff.getTime() >= new Date(session.startTimestamp).getTime()) return null;
      return cutoff.toLocaleString(i18n.language, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: DEFAULT_TIMEZONE,
      });
    },
    [i18n.language]
  );

  // Shared session card renderer
  const renderSessionCard = (session: Session, index: number, sessionDate: Date) => {
    const spotsLeft = getSpotsLeft(session);
    const isSelected = selectedSession === index;
    const isSoldOut = spotsLeft <= 0;
    const isEnded = isSessionEnded(session);
    const isClosed = !isEnded && isSessionClosed(session);
    const isExpired = isEnded || isClosed;
    // Expired/closed sessions are non-interactive. Sold-out sessions remain
    // selectable so the user can join the waitlist via the PriceCard CTA.
    const isDisabled = isExpired;
    const spotsPercentage = (spotsLeft / session.maxCapacity) * 100;
    const showLowSpotsWarning = !isExpired && spotsPercentage <= 50 && spotsLeft <= 3;
    const closesLabel = !isExpired ? registrationClosesLabel(session) : null;
    const effectiveChildPrice = session.price ?? activityPrice;
    const effectiveAdultPrice = session.adultPrice ?? activityAdultPrice;
    const shouldShowPrice = true;

    let cardClassName: string;
    if (isDisabled) {
      cardClassName = 'bg-gray-100 border-gray-300 cursor-not-allowed opacity-60';
    } else if (isSelected) {
      cardClassName = 'bg-primary-pale border-primary shadow-sm';
    } else if (isSoldOut) {
      cardClassName = 'bg-gray-50 border-gray-200 hover:border-primary hover:shadow-sm';
    } else {
      cardClassName = 'bg-white border-gray-200 hover:border-primary hover:shadow-sm';
    }

    return (
      <button
        key={index}
        onClick={() => !isDisabled && selectSessionWithSync(index, sessionDate)}
        disabled={isDisabled}
        className={`w-full text-left p-3 rounded-lg border-2 transition-all ${cardClassName}`}
      >
        {/* Date label */}
        <div className="text-xs text-gray-500 font-medium mb-1">
          {formatDateWithWeekday(sessionDate)}
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-sm font-medium text-gray-900">
              {session.startTime} - {session.endTime}
            </span>
          </div>

          {/* Spots Left / Sold Out / Expired / Closed Badge */}
          {isEnded ? (
            <span className="inline-block bg-gray-500 text-white text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap">
              {t('activity:session.expired')}
            </span>
          ) : isClosed ? (
            <span className="inline-block bg-gray-500 text-white text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap">
              {t('activity:session.closed')}
            </span>
          ) : isSoldOut ? (
            <span className="inline-block bg-gray-500 text-white text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap">
              {t('activity:session.soldOut')}
            </span>
          ) : showLowSpotsWarning ? (
            <span className="inline-block bg-orange text-white text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap">
              {t('activity:session.spotsLeft', { count: spotsLeft })}
            </span>
          ) : null}
        </div>

        {/* Session description */}
        {session.description && (
          <p className="text-xs text-gray-500 mt-1 pl-6">{session.description}</p>
        )}

        {/* Registration deadline hint (only when earlier than session start) */}
        {closesLabel && (
          <p className="text-xs text-orange mt-1 pl-6">
            {t('activity:session.registrationCloses', { time: closesLabel })}
          </p>
        )}

        {/* Session price */}
        {shouldShowPrice && (
          <div className="mt-2 pl-6 flex items-baseline gap-1">
            {isAdultOnly ? (
              <>
                <span className="text-base font-bold text-primary">
                  ${formatPrice(effectiveAdultPrice)}
                </span>
                <span className="text-sm text-gray-500">{t('common:pricing.perAdult')}</span>
              </>
            ) : (
              <>
                <span className="text-base font-bold text-primary">
                  ${formatPrice(effectiveChildPrice)}
                </span>
                <span className="text-sm text-gray-500">{t('common:pricing.perChild')}</span>
                {effectiveAdultPrice > 0 && (
                  <>
                    <span className="text-gray-300 mx-1">|</span>
                    <span className="text-base font-bold text-primary">
                      ${formatPrice(effectiveAdultPrice)}
                    </span>
                    <span className="text-sm text-gray-500">{t('common:pricing.perAdult')}</span>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </button>
    );
  };

  if (!sessions || sessions.length === 0) {
    return <div className="text-center py-4 text-gray-500 text-sm">{t('session.noSessions')}</div>;
  }

  return (
    <div className="space-y-4">
      {/* View Toggle */}
      <div className="flex bg-gray-100 rounded-lg p-1">
        <button
          onClick={() => setViewMode('calendar')}
          className={`flex-1 text-center text-sm font-medium py-2 rounded-md transition-all ${
            viewMode === 'calendar'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('activity:session.calendarView')}
        </button>
        <button
          onClick={() => setViewMode('list')}
          className={`flex-1 text-center text-sm font-medium py-2 rounded-md transition-all ${
            viewMode === 'list'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('activity:session.allSessionsView')}
        </button>
      </div>

      {viewMode === 'calendar' ? (
        <>
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={() => navigateMonth(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <h3 className="text-base font-semibold text-gray-900">
              {formatMonthYear(currentMonth)}
            </h3>
            <button
              onClick={() => navigateMonth(1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="bg-white rounded-lg border border-gray-200 p-2">
            {/* Week Day Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {weekDays.map((day, idx) => (
                <div
                  key={`${day}-${idx}`}
                  className="text-center text-xs font-medium text-gray-500 py-2"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((date, idx) => {
                if (!date) {
                  return <div key={`empty-${idx}`} className="aspect-square" />;
                }

                const dateKey = getLocalDateString(date);
                const isSelected = selectedDateKey === dateKey;
                const past = isPast(date);
                const hasAvailableSession = hasSession(date);
                const isToday = date.toDateString() === today.toDateString();

                return (
                  <button
                    key={idx}
                    onClick={() => hasAvailableSession && selectDate(date)}
                    disabled={!hasAvailableSession}
                    className={`aspect-square flex items-center justify-center rounded-lg text-sm font-medium transition-all ${
                      isSelected
                        ? 'bg-primary text-white shadow-md'
                        : hasAvailableSession && !past
                          ? 'bg-primary-pale text-gray-900 hover:bg-primary hover:text-white cursor-pointer'
                          : hasAvailableSession && past
                            ? 'bg-gray-200 text-gray-500 hover:bg-gray-300 cursor-pointer'
                            : 'text-gray-400 cursor-not-allowed'
                    } ${isToday && !isSelected ? 'ring-2 ring-primary ring-offset-1' : ''}`}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time Slots for Selected Date */}
          {selectedDate && availableSessions.length > 0 && (
            <div className="space-y-2">
              {availableSessions.map(({ session, index }) =>
                renderSessionCard(session, index, selectedDate)
              )}
            </div>
          )}
        </>
      ) : (
        /* All Sessions List View — cap height to roughly match calendar view; scroll inside */
        <div className="max-h-[520px] overflow-y-auto pr-1 space-y-4">
          {bookableSessions.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">
              {t('activity:session.noBookableSessions')}
            </div>
          ) : (
            bookableSessions.map(({ date, sessions: dateSessions }) => (
              <div key={getLocalDateString(date)} className="space-y-2">
                {dateSessions.map(({ session, index }) => renderSessionCard(session, index, date))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
